import { imageNames } from "./imageConstants.js";

export const quickActionsTypes = {
  EMAIL: "email",
  CALL: "call",
  ADD_TO_LIST: "add_to_list",
  ADD_TO_SEQUENCE: "add_to_sequence",
  MARKET_RESEARCH: "market_research",
  NOTE: "note",
  LOG_CALL: "log_call",
  LOG_SMS: "log_sms",
  LOG_WHATSAPP: "log_whatsapp",
  LOG_LINKEDIN: "log_linkedin",
  LOG_MEETING: "log_meeting",
};

// Available actions for the Action property type configuration
export const actionPropertyOptions = [
  { id: "email", label: "Send Email", icon: imageNames.EMAIL_GREY },
  { id: "call", label: "Call", icon: imageNames.CALL_GREY },
  { id: "note", label: "Note", icon: imageNames.NOTE_GREY },
  {
    id: "add_to_list",
    label: "Add to List",
    icon: imageNames.ORDERED_LIST_GREY,
  },
  {
    id: "add_to_sequence",
    label: "Add to Sequence",
    icon: imageNames.ADD_TO_SEQUENCE_GREY,
  },
  { id: "log_call", label: "Log Call", icon: imageNames.CALL_GREY },
  { id: "log_sms", label: "Log SMS", icon: imageNames.SMS_GREY },
  { id: "log_whatsapp", label: "Log WhatsApp", icon: imageNames.WHATSAPP_GREY },
  {
    id: "log_linkedin",
    label: "Log LinkedIn",
    icon: imageNames.SUITCASE_LIGHT,
  },
  { id: "log_meeting", label: "Log Meeting", icon: imageNames.MEETING_GREY },
  {
    id: "market_research",
    label: "Market Research",
    icon: imageNames.MARKET_RESEARCH_GREY,
  },
];

// Supported actions per entity type (uses Set for O(1) lookup)
const SUPPORTED_ACTIONS = {
  contacts: new Set([
    "email",
    "call",
    "note",
    "add_to_list",
    "add_to_sequence",
    "log_call",
    "log_sms",
    "log_whatsapp",
    "log_linkedin",
    "log_meeting",
    "market_research",
  ]),
  leads: new Set([
    "email",
    "call",
    "note",
    "add_to_list",
    "add_to_sequence",
    "log_call",
    "log_sms",
    "log_whatsapp",
    "log_linkedin",
    "log_meeting",
    "market_research",
  ]),
  companies: new Set([
    "email",
    "note",
    "log_call",
    "log_sms",
    "log_whatsapp",
    "log_linkedin",
    "log_meeting",
    "market_research",
  ]),
  deals: new Set([
    "email",
    "note",
    "log_call",
    "log_sms",
    "log_whatsapp",
    "log_linkedin",
    "log_meeting",
  ]),
};

// Default for custom objects
const DEFAULT_SUPPORTED_ACTIONS = new Set(["note"]);

export const getActionOptionsForEntity = (entityType) => {
  const supported = SUPPORTED_ACTIONS[entityType] || DEFAULT_SUPPORTED_ACTIONS;
  return actionPropertyOptions.filter((opt) => supported.has(opt.id));
};

export const ACTIVITY_TAB_COMPONENT_KEYS = [
  "activity",
  "notes",
  "comments",
  "emails",
  "calls",
  "meetings",
  "linkedin",
  "campaigns",
  "sms",
  "whatsapp",
  "tasks",
  "market_research",
  "colleagues",
  "employees",
  "slack_app",
  "livechat",
  "whatsapp_activity",
  "form_submission",
];

export const isActivityTabComponent = (componentKey) => {
  return ACTIVITY_TAB_COMPONENT_KEYS.includes(componentKey);
};

export const DETAIL_PAGE_DROPDOWN_ITEMS = {
  CUSTOMIZE: {
    key: "customize",
    icon: imageNames.EDIT,
    text: "Customize page",
  },
  DELETE: {
    key: "delete",
    icon: imageNames.DELETE_GREY,
    text: "Delete",
  },
};

export const COMPONENT_KEY_MAP = {
  profile_card: "UnifiedProfileCard",
  property_group: "DetailsSection",
  property_details_standard: "StandardPropertyDetails",
  activity_summary: "ActivitySummary",
  activity: "Activity",
  notes: "LoggedNote",
  comments: "LoggedComments",
  emails: "LoggedEmail",
  calls: "LoggedCall",
  meetings: "LoggedMeeting",
  linkedin: "LoggedLinkedIn",
  campaigns: "LoggedCampaign",
  sms: "LoggedSms",
  whatsapp: "LoggedWhatsApp",
  tasks: "CRMTasks",
  colleagues: "Colleagues",
  employees: "CompanyEmployees",
  market_research: "MarketResearch",
  object_hierarchy: "HierarchySection",
  associations: "AssociationsContainer",
  associations_table: "AssociationsTableComponent",
  lists: "ListsAssociation",
  sequences: "SequencesAssociation",
  companies_association: "CompaniesAssociation",
  contacts_association: "ContactsAssociation",
  leads_association: "LeadsAssociation",
  deals_association: "DealsAssociation",
  object: "ObjectNameComponent",
  analytics: "AnalyticsComponent",
  i_assistant: "IAssistantComponent",
  ai_card: "AICardComponent",
  slack_app: "SlackAppComponent",
  whatsapp_activity: "LoggedWhatsAppActivity",
  livechat: "LoggedLiveChat",
  alerts: null,
  signals: null,
  form_submission: "FormSubmissionActivity",
  action_buttons: "ActionButtonsComponent",
  entity_chat: "EntityChat",
  invoice_profile_card: "InvoiceProfileCard",
  invoice_activity_info: "InvoiceActivityInfo",
  invoice_summary: "InvoiceReadView",
  invoice_line_items: "InvoiceLineItemsTab",
  invoice_dft: "InvoiceDiscountFeeTaxTab",
  agreement_profile_card: "AgreementProfileCardComponent",
  agreement_basic_info: "AgreementBasicInfoComponent",
  agreement_activity_info: "AgreementActivityInfoComponent",
  agreement_documents: "AgreementDocumentsComponent",
  agreement_owner: "AgreementOwnerComponent",
  agreement_recipients: "AgreementRecipientsComponent",
};

export const ASSOCIATION_COMPONENTS = [
  "lists",
  "sequences",
  "companies_association",
  "contacts_association",
  "leads_association",
  "deals_association",
];

export const COMPONENTS_WITH_OWN_CONTAINER = [
  "lists",
  "sequences",
  "associations",
  "companies_association",
  "contacts_association",
  "leads_association",
  "deals_association",
  "ai_card",
];

export const hasOwnContainer = (componentKey) => {
  return COMPONENTS_WITH_OWN_CONTAINER.includes(componentKey);
};

export const getComponentName = (componentKey) => {
  return COMPONENT_KEY_MAP[componentKey] || null;
};

// Action button layout constants (padding + border + icon + gap = 38px base, ~5.4px per char at 11px font)
const ACTION_BUTTON_BASE_WIDTH = 38;
const ACTION_BUTTON_CHAR_WIDTH = 5.4;

export const ACTION_BUTTON_WIDTHS = Object.fromEntries(
  actionPropertyOptions.map((opt) => [
    opt.id,
    Math.round(
      ACTION_BUTTON_BASE_WIDTH + opt.label.length * ACTION_BUTTON_CHAR_WIDTH,
    ),
  ]),
);
export const MORE_BUTTON_WIDTH = Math.round(
  ACTION_BUTTON_BASE_WIDTH + 4 * ACTION_BUTTON_CHAR_WIDTH,
);
export const ACTION_BUTTON_GAP = 6;

export const ENRICHABLE_ENTITY_MAP = { contacts: "contact", leads: "lead" };
